<template>
  <IonHeader ref="modal">
    <IonToolbar>
      <IonTitle>Reset Password</IonTitle>
      <IonButtons slot="end">
        <IconButton @click="modalController.dismiss()" icon="icon-x"></IconButton>
      </IonButtons>
    </IonToolbar>
  </IonHeader>
  <IonContent>
    <PasswordForm @on-password="onPassword($event)" :include-current-password="includeCurrentPassword" :token="token">
      <div class="text-gray-500 font-medium px-4">Create a new password</div>
      <div class="text-gray-500 font-normal text-sm mb-4 px-4">Password must be at least 8 characters long</div>
    </PasswordForm>
  </IonContent>
  <IonFooter class="ion-no-border pb-4">
    <hr class="mx-3" />
    <IonToolbar color="white">
      <PrimaryBlockButton class="mt-6 mx-3" :disabled="!canSave" @click="submit" type="button">Save</PrimaryBlockButton>
    </IonToolbar>
  </IonFooter>
</template>

<script setup lang="ts">
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonContent,
  IonFooter,
  IonModal,
  modalController,
} from '@ionic/vue';
import IconButton from '@/components/forms/IconButton.vue';
import PrimaryBlockButton from '@/components/forms/PrimaryBlockButton.vue';
import PasswordForm, { IPasswordEmit } from '@/components/user-account/PasswordForm.vue';
import { ref } from 'vue';
import { UserService } from '@/http-services/user.service';
import { useToast } from '@/composables/use-toast';
import { AxiosError } from 'axios';

const modal = ref<InstanceType<typeof IonModal>>();
const userService = new UserService();
const toast = useToast();
const currentPassword = ref<string | undefined>();
const newPassword = ref('');
const confirmPassword = ref('');
const canSave = ref(false);
const saving = ref(false);
const props = withDefaults(defineProps<{ token?: string; includeCurrentPassword?: boolean }>(), {
  includeCurrentPassword: false,
});

function onPassword(e: IPasswordEmit) {
  const { valid, currentPassword: currentP, newPassword: newP, confirmPassword: confirmP } = e;
  canSave.value = valid && !saving.value;
  currentPassword.value = currentP;
  newPassword.value = newP;
  confirmPassword.value = confirmP;
}

async function submit() {
  saving.value = true;
  try {
    const res = await userService.updatePassword(
      newPassword.value,
      confirmPassword.value,
      props.token,
      currentPassword.value
    );
    if (res.success) {
      modalController.dismiss();
      toast.successToast(res.message, { duration: 3000 });
    } else {
      toast.errorToast(res.message, { duration: 3000 });
    }
  } catch (e) {
    toast.errorToast((e as AxiosError<{ error: string }>).response?.data?.error || 'Error updating password', {
      duration: 3500,
    });
  } finally {
    saving.value = false;
  }
}
</script>

<style scoped></style>
